import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import config from "../config";
import { Eye, EyeOff, Loader2, User, Mail, Lock, Check, AlertCircle } from "lucide-react";

export default function Register() {
  const [formData, setFormData] = useState({ fullName: "", email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const navigate = useNavigate();

  const toggleVisibility = () => setIsVisible(!isVisible);

  const isFieldValid = (name) => {
    const value = formData[name];
    if (!value) return false;
    if (name === "fullName") return value.trim().length > 0;
    if (name === "email") return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
    if (name === "password") return value.length >= 6;
    return true;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required.";
    }
    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!isFieldValid("email")) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else if (!isFieldValid("password")) {
      newErrors.password = "Password must be at least 6 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (globalError) {
      setGlobalError("");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setGlobalError("");

    try {
      const response = await fetch(`${config.API_BASE_URL}/Auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          password: formData.password,
        }),
      });

      const data = await response.json().catch(() => ({ message: "Failed to parse server response." }));
      if (!response.ok) throw new Error(data.message || "Registration failed. Please try again.");

      setIsSuccess(true);
      await new Promise(resolve => setTimeout(resolve, 800)); 

      navigate("/login", { state: { message: data.message || "Registration successful! You can now log in." } });
    } catch (error) {
      setGlobalError(error.message || "An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const inputBaseClasses = (name) => {
    const hasError = !!errors[name];
    const isValid = isFieldValid(name) && !hasError;
    
    return `
      block w-full px-3.5 pb-2 pt-6 border rounded-input shadow-small 
      appearance-none focus:outline-none focus:ring-0 transition-all 
      text-text-primary text-[14px] bg-bg-soft peer
      ${hasError 
        ? "border-status-pending focus:border-status-pending bg-bg-warning/50" 
        : isValid 
          ? "border-status-complete focus:border-status-complete bg-bg-success/20"
          : "border-border-light focus:border-primary"
      }
    `;
  };

  const labelBaseClasses = (name) => {
    const hasError = !!errors[name];
    const isValid = isFieldValid(name) && !hasError;

    return `
      absolute text-[14px] text-text-secondary duration-300 transform 
      -translate-y-4 scale-75 top-4 z-10 origin-[0] start-3.5 
      peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 
      peer-focus:scale-75 peer-focus:-translate-y-4 
      ${hasError ? "text-feedback-warning" : isValid ? "text-feedback-success" : "peer-focus:text-primary"}
      inline-flex items-center pointer-events-none font-semibold
    `;
  };

  const shakeAnimation = {
    x: [0, -6, 6, -6, 6, 0],
    transition: { duration: 0.4 }
  };

  const containerVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.4,
        staggerChildren: 0.08,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-bg-app flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden text-[14px]">
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        <div className="flex justify-center mb-6">
          <img src="/logo4.png" alt="JHY Techs" className="h-16 w-auto object-contain" />
        </div>
        <h2 className="text-center text-[24px] font-bold text-text-primary tracking-tight">
          Create an account
        </h2>
        <p className="mt-2 text-center text-text-secondary">
          Already have an account?{" "}
          <motion.span whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-block">
            <Link to="/login" className="font-medium text-primary hover:text-primary-hover transition-colors">
              Sign in here
            </Link>
          </motion.span>
        </p>
      </motion.div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        <div className="bg-bg-card py-8 px-4 shadow-card sm:rounded-card sm:px-10 border border-border-light">
          
          <AnimatePresence>
            {globalError && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, height: 0 }}
                className="p-4 mb-6 bg-bg-warning border border-status-pending text-text-primary rounded-input text-sm flex items-start gap-3 overflow-hidden shadow-small"
              >
                <AlertCircle className="w-5 h-5 text-feedback-warning mt-0.5 flex-shrink-0" />
                <p className="font-medium text-[14px]">{globalError}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <form className="space-y-5" onSubmit={handleRegister}>
            <motion.div variants={itemVariants} animate={errors.fullName ? shakeAnimation : {}} className="relative">
              <input
                id="fullName"
                name="fullName"
                type="text"
                autoComplete="name"
                placeholder=" "
                value={formData.fullName}
                onChange={handleChange}
                disabled={isLoading}
                className={inputBaseClasses("fullName")}
              />
              <label htmlFor="fullName" className={labelBaseClasses("fullName")}>
                <User className="w-4 h-4 me-2 transition-colors" />
                Full name
              </label>
              {errors.fullName && <p className="mt-1.5 text-[12px] text-accent-orange font-medium">{errors.fullName}</p>}
            </motion.div>

            <motion.div variants={itemVariants} animate={errors.email ? shakeAnimation : {}} className="relative">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder=" "
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                className={inputBaseClasses("email")}
              />
              <label htmlFor="email" className={labelBaseClasses("email")}>
                <Mail className="w-4 h-4 me-2 transition-colors" />
                Email address
              </label>
              {errors.email && <p className="mt-1.5 text-[12px] text-accent-orange font-medium">{errors.email}</p>}
            </motion.div>

            <motion.div variants={itemVariants} animate={errors.password ? shakeAnimation : {}} className="relative">
              <input
                id="password"
                name="password"
                type={isVisible ? "text" : "password"}
                autoComplete="new-password"
                placeholder=" "
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
                className={inputBaseClasses("password")}
              />
              <label htmlFor="password" className={labelBaseClasses("password")}>
                <Lock className="w-4 h-4 me-2 transition-colors" />
                Password
              </label>
              <motion.button
                type="button"
                onClick={toggleVisibility}
                whileTap={{ scale: 0.8 }}
                disabled={isLoading}
                className="absolute top-[22px] right-3.5 flex items-center text-text-secondary hover:text-text-primary transition-colors z-20"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={isVisible ? "visible" : "hidden"}
                    initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.5, rotate: 45 }}
                    transition={{ duration: 0.15 }}
                  >
                    {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </motion.div>
                </AnimatePresence>
              </motion.button>
              {errors.password && <p className="mt-1.5 text-[12px] text-accent-orange font-medium">{errors.password}</p>}
            </motion.div>

            <motion.div variants={itemVariants} className="flex justify-center pt-2">
              <motion.button
                type="submit"
                disabled={isLoading}
                animate={{
                  width: isSuccess ? "48px" : "100%",
                  backgroundColor: isSuccess ? "var(--color-status-complete)" : "var(--color-primary)",
                  borderRadius: isSuccess ? "24px" : "8px",
                }}
                whileHover={!isSuccess ? { scale: 1.01, backgroundColor: "var(--color-primary-hover)" } : {}}
                whileTap={!isSuccess ? { scale: 0.98 } : {}}
                className="flex justify-center items-center py-[12px] border border-transparent shadow-small text-[14px] font-semibold text-white transition-colors disabled:opacity-70 min-h-[46px]"
              >
                <AnimatePresence mode="wait">
                  {isSuccess ? (
                    <motion.div key="success" initial={{ scale: 0 }} animate={{ scale: 1 }}><Check className="w-6 h-6" /></motion.div>
                  ) : isLoading ? (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                      <Loader2 className="animate-spin h-4 w-4" /> Creating account...
                    </motion.div>
                  ) : (
                    <motion.span key="text" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>Create account</motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
            
            <motion.p variants={itemVariants} className="text-center text-[12px] text-text-muted mt-4">
              By registering, you agree to our <br/>
              <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} href="https://www.jhytechs.com/terms.html" className="text-primary hover:text-primary-hover transition-colors font-medium">Terms of Service</motion.a> and{" "}
              <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} href="https://www.jhytechs.com/privacypolicy.html" className="text-primary hover:text-primary-hover transition-colors font-medium">Privacy Policy</motion.a>
            </motion.p>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
